import { useState } from "react";
import { apiFetch } from "../utils/api"; // adjust path
import { USER_BASE_URL, VERSION } from "../utils/config";
import { formatTime } from "../utils/time";
import { showToast } from "../toastService";
import VideoModal from "./VideoModal";

export default function ModuleItem({
  module,
  index,
  hasFullAccess = false,
  unlockedModuleIds = [],
}) {
  const moduleUnlocked =
    hasFullAccess ||
    (Array.isArray(unlockedModuleIds) && unlockedModuleIds.includes(String(module._id)));
  const hasPartialUnlock = !hasFullAccess && Array.isArray(unlockedModuleIds) && unlockedModuleIds.length > 0;

  const canAccessLesson = (lesson) => {
    if (hasFullAccess || moduleUnlocked) return true;
    if (hasPartialUnlock) return false;
    return module.index === 1 && lesson.index === 1;
  };
  const [open, setOpen] = useState(false);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [videoData, setVideoData] = useState(null); // { otp, playbackInfo, title } etc.
  const [showPlayer, setShowPlayer] = useState(false);
  const [error, setError] = useState("");

  const handleLessonClick = async (lesson) => {
    if (lesson.type !== "Video") {
      showToast("Not Supported", "error");
      return;
    }

    showToast("Fetching data...", "success");

    try {
      const res = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/lesson/web/`,
          "POST",
          JSON.stringify({ id: lesson._id }),
          { "Content-Type": "application/json" }
        );
      if(res && res.protected_data){
        setVideoData({
          ...res.protected_data,
          title: lesson.name
        });
        setShowPlayer(true);
      }else{
        showToast("Failed to load video data", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to load video data", "error");
    }
  };

  const toggleOpen = async () => {
    setOpen(prev => !prev);
    if (!open && lessons.length === 0) {
      setLoading(true);
      setError("");
      try {
        const res = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/course/web/lesson-list`,
          "POST",
          JSON.stringify({ id: module._id }),
          { "Content-Type": "application/json" }
        );
        if (res && res.lessons) {
          const sorted = res.lessons
            .sort((a, b) => a.index - b.index);
          setLessons(sorted);
        } else {
          setError("No lessons found");
        }
      } catch (e) {
        setError("Failed to load lessons");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className={`module-item ${open ? "open" : ""}`}>
      <button
        className="module-title"
        onClick={toggleOpen}
        aria-expanded={open}
      >
        Module {index + 1}: {module.name}
        <span className="module-title-end">
          {!hasFullAccess &&
            !moduleUnlocked &&
            (hasPartialUnlock || module.index !== 1) && (
              <span className="lock-icon module-lock" />
            )}
          <span className="arrow" />
        </span>
      </button>

      {open && (
        <div className="module-content">
          {loading && (
            <div className="spinner-div">
              Loading… <span className="loader-blue"></span>
            </div>
          )}

          {error && <div className="error-text">{error}</div>}

          {!loading && !error && (
            <ul className="lesson-list">
              {lessons.map((lesson) => (
                <li key={lesson._id} className="lesson-item" 
                  onClick={() => {
                    if (canAccessLesson(lesson)) {
                      handleLessonClick(lesson);
                    } else {
                      showToast(
                        hasPartialUnlock
                          ? "Purchase the full course to view this module"
                          : "Purchase course to view more",
                        "error"
                      );
                    }
                  }}
                >
                  <div className="lesson-icon">
                    {lesson.type === "Video" && <span className="icon-video" />}
                    {lesson.type === "PDF" && <span className="icon-pdf" />}
                    {lesson.type === "FlipCards" && <span className="icon-cards" />}
                    {lesson.type === "Test" && <span className="icon-test" />}
                  </div>
                  <div className="lesson-info">
                    <div className="lesson-title">{lesson.name}</div>
                    <div className="lesson-meta">
                      {lesson.type === "Video" ? (
                        <>
                          Lesson {lesson.index} • {lesson.type}
                          {lesson.content?.video_data?.length && (
                            <> • {formatTime(lesson.content.video_data.length)}</>
                          )}
                        </>
                      ) : (
                        <>Reading</>
                      )}
                    </div>
                  </div>
                  {!canAccessLesson(lesson) && (
                    <div className="lesson-lock">
                      <span className="lock-icon" />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showPlayer && videoData && (
        <VideoModal videoData={videoData} onClose={() => setShowPlayer(false)} />
      )}
    </div>
  );
}